import { IonCard, IonCardContent } from '@ionic/react';
import { ReactElement, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper as SwiperReact, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { WeldSettingsRecommendation } from './settings-recommendation.hook';
import './weld-settings-recommendation-cards.css';

interface WeldSettingsRecommendationCardsProps {
  recommendations: WeldSettingsRecommendation[];
  selectedIndex?: number;
  equipment?: string;
  onSelectRecommendation: (
    index: number,
    recommendation: WeldSettingsRecommendation
  ) => void;
}

// Helper function to determine if a welder supports arc length parameter
// ESAB uses "trim" instead, OTC may have different parameter naming
const welderSupportsArcLength = (equipment?: string): boolean => {
  const weldersWithoutArcLength = [
    BeaconWeldingData.Welder.ESAB_ARISTO,
    BeaconWeldingData.Welder.OTC_WELBEE,
  ];
  return !weldersWithoutArcLength.includes(
    equipment as BeaconWeldingData.Welder
  );
};

export const WeldSettingsRecommendationCards = ({
  recommendations,
  selectedIndex = 0,
  equipment,
  onSelectRecommendation,
}: WeldSettingsRecommendationCardsProps): ReactElement => {
  const [selected, setSelected] = useState(selectedIndex);

  const handleCardClick = (
    index: number,
    recommendation: WeldSettingsRecommendation
  ) => {
    setSelected(index);
    onSelectRecommendation(index, recommendation);
  };

  const getParameterDisplay = (
    recommendation: WeldSettingsRecommendation,
    paramRef: string
  ) => {
    const param = recommendation.parameters.find((p) => p.ref === paramRef);
    if (!param) return null;

    return {
      value: param.value,
      min: param.minValue,
      max: param.maxValue,
    };
  };

  const formatParameterRange = (
    min: number | undefined,
    max: number | undefined
  ) => {
    if (min === undefined || max === undefined) return '';
    return `${min} - ${max}`;
  };

  const getProcessLabel = (process: string): string => {
    const processLabels: Record<string, string> = {
      [BeaconWeldingData.WeldProcess.CV]:
        getLocalizedText('welding:cv') || 'CV',
      [BeaconWeldingData.WeldProcess.PULSE]:
        getLocalizedText('welding:pulse') || 'Pulse',
      [BeaconWeldingData.WeldProcess.MILLER_RMD]:
        getLocalizedText('welding:rmd') || 'RMD',
      [BeaconWeldingData.WeldProcess.MILLER_HD]:
        getLocalizedText('welding:hd') || 'HD',
      [BeaconWeldingData.WeldProcess.OTC_WAVE_PULSE]:
        getLocalizedText('welding:wave_pulse') || 'Wave Pulse',
    };
    return processLabels[process] || process;
  };

  return (
    <div className="weld-settings-recommendation-cards-container">
      <SwiperReact
        spaceBetween={12}
        slidesPerView={Math.min(3, recommendations.length)}
        navigation={recommendations.length > 3}
        modules={[Navigation]}
        className="weld-settings-recommendation-swiper"
      >
        {recommendations.slice(0, 3).map((recommendation, index) => {
          // Get the process for this specific recommendation
          const recommendationProcess = getParameterDisplay(
            recommendation,
            BeaconWeldingData.FlowItemParameterRef.PROCESS
          );

          // Determine if this specific recommendation uses pulse process
          const isThisRecommendationPulseProcess =
            recommendationProcess?.value ===
              BeaconWeldingData.WeldProcess.PULSE ||
            recommendationProcess?.value ===
              BeaconWeldingData.WeldProcess.OTC_WAVE_PULSE;

          const travelSpeed = getParameterDisplay(
            recommendation,
            BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED
          );
          const wireFeedSpeed = getParameterDisplay(
            recommendation,
            BeaconWeldingData.FlowItemParameterRef.WIRE_FEED_SPEED
          );

          // Show voltage for non-pulse processes (CV, etc.)
          const voltage = !isThisRecommendationPulseProcess
            ? getParameterDisplay(
                recommendation,
                BeaconWeldingData.FlowItemParameterRef.VOLTAGE
              )
            : null;

          // Show arc length for pulse processes, but only for welders that support it
          const shouldShowArcLength =
            isThisRecommendationPulseProcess &&
            welderSupportsArcLength(equipment);
          const arcLength = shouldShowArcLength
            ? getParameterDisplay(
                recommendation,
                BeaconWeldingData.FlowItemParameterRef.ARC_LENGTH
              )
            : null;

          // Show process type for pulse welders that don't support arc length
          const shouldShowProcess =
            isThisRecommendationPulseProcess &&
            !welderSupportsArcLength(equipment) &&
            recommendationProcess?.value;

          return (
            <SwiperSlide key={index}>
              <IonCard
                className={`weld-settings-recommendation-card ${
                  selected === index ? 'selected' : ''
                }`}
                onClick={() => handleCardClick(index, recommendation)}
              >
                <IonCardContent>
                  <div className="recommendation-header">
                    <span className="recommendation-label">
                      {index === 0
                        ? getLocalizedText('common:best')
                        : index === 1
                          ? getLocalizedText('common:better')
                          : getLocalizedText('common:good')}
                    </span>
                  </div>

                  <div className="recommendation-params">
                    {travelSpeed && (
                      <div className="param-group">
                        <div className="param-label">
                          {getLocalizedText('welding:travel_speed')}
                        </div>
                        <div className="param-value">{travelSpeed.value}</div>
                        <div className="param-range">
                          {formatParameterRange(
                            travelSpeed.min,
                            travelSpeed.max
                          )}
                        </div>
                      </div>
                    )}

                    {wireFeedSpeed && (
                      <div className="param-group">
                        <div className="param-label">
                          {getLocalizedText('welding:wire_feed_speed')}
                        </div>
                        <div className="param-value">{wireFeedSpeed.value}</div>
                        <div className="param-range">
                          {formatParameterRange(
                            wireFeedSpeed.min,
                            wireFeedSpeed.max
                          )}
                        </div>
                      </div>
                    )}

                    {voltage && (
                      <div className="param-group">
                        <div className="param-label">
                          {getLocalizedText('welding:voltage')}
                        </div>
                        <div className="param-value">{voltage.value}</div>
                        <div className="param-range">
                          {formatParameterRange(voltage.min, voltage.max)}
                        </div>
                      </div>
                    )}

                    {arcLength && (
                      <div className="param-group">
                        <div className="param-label">
                          {getLocalizedText('welding:arc_length')}
                        </div>
                        <div className="param-value">{arcLength.value}</div>
                        <div className="param-range">
                          {formatParameterRange(arcLength.min, arcLength.max)}
                        </div>
                      </div>
                    )}

                    {shouldShowProcess && (
                      <div className="param-group">
                        <div className="param-label">
                          {getLocalizedText('common:process') || 'Process'}
                        </div>
                        <div className="param-value">
                          {getProcessLabel(recommendationProcess.value)}
                        </div>
                      </div>
                    )}
                  </div>
                </IonCardContent>
              </IonCard>
            </SwiperSlide>
          );
        })}
      </SwiperReact>
    </div>
  );
};
